import { WORKER_RUN_QUEUE_POLL_INTERVAL_MS } from "#src/constants/workerRunQueuePolling";
import {
  useRunWorkerDrawer,
  type RunWorkerDrawerQueuedRunType,
} from "#src/contexts/RunWorkerDrawer";
import { useQueueUnique } from "#src/hooks/useQueue";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { QueueUnique } from "#src/utils/queue";
import { useQuery } from "@tanstack/react-query";
import {
  WorkerRunAdapter,
  WorkerRunAdapterQueryKeys,
  WorkerRunStatus,
  type WorkerRunType,
} from "@validereinc/domain";
import { useEffect, useMemo } from "react";

/** Batch list response plus the ids that request was for (distinguish missing row vs still loading). */
type WorkerRunQueueBatchPollQueryDataType = Awaited<
  ReturnType<typeof WorkerRunAdapter.getList>
> & {
  requestedPollIds: string[];
};

/** Per-queue-index snapshot aligned with {@link useWorkerRunQueue}'s `runIds` (queue `polledRun`). */
export type WorkerRunQueuePollEntryType = {
  data: WorkerRunType | undefined;
  dataUpdatedAt: number;
};

/**
 * True if any requested id is present and still `started` (needs another poll).
 * Ids missing from the search response are treated as terminal for polling (not in-flight).
 */
const batchResponseStillHasInFlightRuns = (
  runs: WorkerRunType[],
  requestedPollIds: string[]
): boolean => {
  const runById = new Map(runs.map((run) => [run.id, run]));
  return requestedPollIds.some((id) => {
    const run = runById.get(id);
    if (run == null) return false;
    return run.status === WorkerRunStatus.STARTED;
  });
};

/**
 * Merges list poll rows into the queue and marks ids that were requested but absent from the response.
 */
const mergeBatchPollIntoQueuedRuns = (
  currentQueue: QueueUnique<RunWorkerDrawerQueuedRunType>,
  runs: WorkerRunType[],
  requestedPollIds: string[]
): QueueUnique<RunWorkerDrawerQueuedRunType> => {
  const runById = new Map(runs.map((run) => [run.id, run]));
  const requestedSet = new Set(requestedPollIds);
  return new QueueUnique(
    currentQueue.getAsArray().map((entry) => {
      if (!requestedSet.has(entry.id)) return entry;
      const run = runById.get(entry.id);
      if (run != null) {
        return {
          ...entry,
          polledRun: run,
          notReturnedBySearchPoll: false,
        };
      }
      return {
        ...entry,
        notReturnedBySearchPoll: true,
      };
    }),
    (queuedRun) => queuedRun.id
  );
};

/**
 * Shared hook for the global worker run queue and polling. Use from GlobalWorkerRunButton
 * (to show running count) and RunWorkerDrawer (to display queue, add runs, and show toasts).
 * Ensures a single set of TanStack Query observers for the queue's run IDs.
 */
export const useWorkerRunQueue = () => {
  const [workerRunQueueStorageKey] = useStorageKey(["queue"], "worker-runs");

  const [queue, setQueue] = useQueueUnique<RunWorkerDrawerQueuedRunType>(
    [],
    workerRunQueueStorageKey,
    (run) => run.id
  );

  const { setStartedWorkerRunsCount } = useRunWorkerDrawer();

  const { runIds, sortedRunIdsToPoll } = useMemo(() => {
    const entries = queue.getAsArray();
    const ids = entries.map((entry) => entry.id);
    const byId = new Map(entries.map((entry) => [entry.id, entry]));
    const sortedUnique = [...new Set(ids)].sort((a, b) => a.localeCompare(b));
    /**
     * Poll when we lack a snapshot (`polledRun` missing, e.g. legacy localStorage)
     * or the last snapshot is still in flight. Skip only known terminal states.
     */
    const toPoll = sortedUnique.filter((id) => {
      const entry = byId.get(id);
      if (entry?.notReturnedBySearchPoll) return false;
      const status = entry?.polledRun?.status;
      if (status == null) return true;
      return status === WorkerRunStatus.STARTED;
    });
    return { runIds: ids, sortedRunIdsToPoll: toPoll };
  }, [queue]);

  const listParams =
    sortedRunIdsToPoll.length > 0
      ? ({
          page: 1,
          pageSize: 500,
          filters: { id: sortedRunIdsToPoll },
        } as const)
      : null;

  const batchPollQuery = useQuery<
    WorkerRunQueueBatchPollQueryDataType | undefined,
    unknown,
    WorkerRunQueueBatchPollQueryDataType | undefined
  >({
    queryKey: listParams
      ? WorkerRunAdapterQueryKeys.getList(listParams)
      : (["worker-run-queue", "idle"] as const),
    keepPreviousData: true,
    queryFn: async () => {
      if (listParams == null) return undefined;
      const result = await WorkerRunAdapter.getList(listParams);
      return {
        ...result,
        requestedPollIds: [...listParams.filters.id],
      };
    },
    enabled: listParams != null,
    refetchInterval: (data) => {
      if (sortedRunIdsToPoll.length === 0) return false;
      const runs = data?.data;
      if (runs == null) {
        return WORKER_RUN_QUEUE_POLL_INTERVAL_MS;
      }
      const requestedPollIds = data?.requestedPollIds ?? sortedRunIdsToPoll;
      return batchResponseStillHasInFlightRuns(runs, requestedPollIds)
        ? WORKER_RUN_QUEUE_POLL_INTERVAL_MS
        : false;
    },
  });

  useEffect(() => {
    const payload = batchPollQuery.data;
    if (payload?.requestedPollIds == null) return;
    const runs = payload.data ?? [];
    setQueue((currentQueue) =>
      mergeBatchPollIntoQueuedRuns(currentQueue, runs, payload.requestedPollIds)
    );
  }, [batchPollQuery.data, setQueue]);

  const runPollQueries = useMemo((): WorkerRunQueuePollEntryType[] => {
    const dataUpdatedAt = batchPollQuery.dataUpdatedAt;
    return runIds.map((id) => ({
      data: queue.findById(id)?.polledRun,
      dataUpdatedAt,
    }));
  }, [batchPollQuery.dataUpdatedAt, runIds, queue]);

  useEffect(() => {
    let count = 0;
    for (const entry of queue.getAsArray()) {
      if (entry.notReturnedBySearchPoll) continue;
      const pollIndex = runIds.indexOf(entry.id);
      const liveRun =
        pollIndex >= 0 ? runPollQueries[pollIndex]?.data : undefined;
      const effectiveStatus = liveRun?.status ?? WorkerRunStatus.STARTED;
      if (effectiveStatus === WorkerRunStatus.STARTED) {
        count += 1;
      }
    }
    setStartedWorkerRunsCount(count);
  }, [queue, runIds, runPollQueries]);

  const dismissableQueuedRunIds = useMemo(() => {
    const ids: string[] = [];
    for (const entry of queue.getAsArray()) {
      const pollIndex = runIds.indexOf(entry.id);
      const liveRun =
        pollIndex >= 0 ? runPollQueries[pollIndex]?.data : undefined;
      if (entry.notReturnedBySearchPoll) {
        ids.push(entry.id);
        continue;
      }
      if (
        liveRun?.status === WorkerRunStatus.COMPLETED ||
        liveRun?.status === WorkerRunStatus.FAILED
      ) {
        ids.push(entry.id);
      }
    }
    return ids;
  }, [queue, runIds, runPollQueries]);

  return { queue, setQueue, runIds, runPollQueries, dismissableQueuedRunIds };
};
